import { createContext, forwardRef, useState, useMemo, useRef, useImperativeHandle, useEffect, useContext } from 'react';
import { useId } from 'react-id-generator';
import MaskedInput from 'react-text-mask';
import { Flex } from '../../Flex/index.js';
import { Message } from '../../forms/Message/index.js';
import { extractElementFromChildren, handleNumberInputWheel } from '../shared/utils.js';
import { InputWrapper, FloatingLabel, StyledInput, LeftElementWrapper, RightElementWrapper } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { Spacer, cssShorthandPropsFilter } from '../../primitives/Spacer/index.js';

const ComposableInputContext = createContext(null);
const useComposableInputContext = () => {
  const context = useContext(ComposableInputContext);
  if (!context) {
    throw new Error('ComposableInput components must be used within a ComposableInput.Root');
  }
  return context;
};
const Root = forwardRef(({
  'data-testid': dataTestId,
  className,
  disabled,
  error,
  id: providedId,
  inputSize = 'md',
  message,
  name,
  successMessage,
  children,
  ...rest
}, ref) => {
  const [uniqueId] = useId(1, 'composable-input-');
  const id = providedId || uniqueId;
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const {
    hasLeftElement,
    hasRightElement,
    hasMessage
  } = useMemo(() => {
    const leftElement = extractElementFromChildren(children, LeftElement);
    const rightElement = extractElementFromChildren(children, RightElement);
    return {
      hasLeftElement: Boolean(leftElement),
      hasRightElement: Boolean(rightElement),
      hasMessage: Boolean(message || error || successMessage)
    };
  }, [children, message, error, successMessage]);
  const contextValue = useMemo(() => ({
    id,
    name,
    inputSize,
    disabled,
    error,
    successMessage,
    isFocused,
    hasValue,
    hasLeftElement,
    setIsFocused,
    setHasValue
  }), [id, name, inputSize, disabled, error, successMessage, isFocused, hasValue, hasLeftElement]);
  return jsx(ComposableInputContext.Provider, {
    value: contextValue,
    children: jsxs(Spacer, {
      ref: ref,
      "data-testid": dataTestId,
      className: className,
      ...cssShorthandPropsFilter(rest),
      children: [jsx(Flex, {
        children: jsx(InputWrapper, {
          hasError: Boolean(error),
          inputSize: inputSize,
          isDisabled: disabled,
          isSuccessful: Boolean(successMessage),
          isFocused: isFocused,
          hasValue: hasValue,
          "data-left-element": hasLeftElement || undefined,
          "data-right-element": hasRightElement || undefined,
          children: children
        })
      }), hasMessage && jsx(Message, {
        "data-testid": `${dataTestId}-message`,
        disabled: disabled,
        error: error,
        id: `${id}-message`,
        message: message,
        successMessage: successMessage
      })]
    })
  });
});
const Label = forwardRef(({
  children
}, ref) => {
  const {
    id,
    inputSize,
    isFocused,
    hasValue,
    disabled,
    error,
    successMessage,
    hasLeftElement
  } = useComposableInputContext();
  return jsx(FloatingLabel, {
    ref: ref,
    htmlFor: id,
    inputSize: inputSize,
    isFocused: isFocused,
    hasValue: hasValue,
    isDisabled: disabled,
    hasError: Boolean(error),
    isSuccessful: Boolean(successMessage),
    hasLeftElement: hasLeftElement,
    children: children
  });
});
const Input = forwardRef(({
  onFocus,
  onBlur,
  onChange,
  value,
  defaultValue,
  mask,
  numeric,
  onWheel,
  ...htmlInputProps
}, ref) => {
  const {
    id,
    name,
    inputSize,
    disabled,
    error,
    isFocused,
    hasValue,
    setIsFocused,
    setHasValue
  } = useComposableInputContext();
  const inputRef = useRef(null);
  const maskedInputRef = useRef(null);
  useImperativeHandle(ref, () => {
    if (mask && maskedInputRef.current) {
      return maskedInputRef.current.inputElement;
    }
    return inputRef.current;
  });
  useEffect(() => {
    if (defaultValue || value) setHasValue(true);
  }, [defaultValue, value, setHasValue]);
  const handleEvents = useMemo(() => ({
    onFocus: e => {
      setIsFocused(true);
      onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    },
    onBlur: e => {
      setIsFocused(false);
      onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    },
    onChange: e => {
      setHasValue(Boolean(e.target.value));
      onChange === null || onChange === void 0 ? void 0 : onChange(e);
    }
  }), [onFocus, onBlur, onChange, setIsFocused, setHasValue]);
  const inputProps = useMemo(() => ({
    'aria-describedby': error ? `${id}-message` : undefined,
    disabled,
    id,
    inputSize,
    name,
    value,
    defaultValue,
    numeric,
    onWheel: onWheel || handleNumberInputWheel,
    hasFloatingLabel: true,
    ...handleEvents,
    ...htmlInputProps
  }), [error, id, disabled, inputSize, name, value, defaultValue, numeric, onWheel, handleEvents, htmlInputProps]);
  if (mask) {
    return jsx(MaskedInput, {
      autoComplete: "off",
      guide: false,
      ref: maskedInputRef,
      mask: mask,
      ...inputProps,
      render: (maskRef, maskProps) => jsx(StyledInput, {
        ...maskProps,
        inputSize: inputSize,
        ref: maskRef,
        isFocused: isFocused,
        hasValue: hasValue,
        "data-main-content": true
      })
    });
  }
  return jsx(StyledInput, {
    ref: inputRef,
    ...inputProps,
    inputSize: inputSize,
    isFocused: isFocused,
    hasValue: hasValue,
    "data-main-content": true
  });
});
const LeftElement = forwardRef(({
  children
}, ref) => {
  const {
    inputSize
  } = useComposableInputContext();
  return jsx(LeftElementWrapper, {
    ref: ref,
    inputSize: inputSize,
    children: children
  });
});
const RightElement = forwardRef(({
  children
}, ref) => {
  const {
    inputSize
  } = useComposableInputContext();
  return jsx(RightElementWrapper, {
    ref: ref,
    inputSize: inputSize,
    children: children
  });
});
Root.displayName = 'ComposableInput.Root';
Label.displayName = 'ComposableInput.Label';
Input.displayName = 'ComposableInput.Input';
LeftElement.displayName = 'ComposableInput.LeftElement';
RightElement.displayName = 'ComposableInput.RightElement';
const ComposableInput = Object.assign(Root, {
  Label,
  Input,
  LeftElement,
  RightElement
});

export { ComposableInput };
